import React, { useState, useEffect, useRef } from 'react';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useAuth } from '../../../context/AuthContext';
import ServiceTypeFilter from '../ServiceTypeFilter/ServiceTypeFilter';
import PriceCheckboxFilter from '../PriceCheckboxFilter/PriceCheckboxFilter';
import AuthModal from '../AuthModal/AuthModal';
import {
  FilterContainer,
  FilterRow,
  FavoritesToggle,
  ClearButton,
} from './BusinessFilter.styled';

const BusinessFilter = ({
  onFilterChange,
  initialFilters = {},
  showServiceTypeFilter = true,
  showPriceFilter = true,
  showFavoritesFilter = false,
  favoritesOnly = false,
  onFavoritesOnlyChange,
  businesses = [],
  requireAuthForFilters = true,
  requireAuthForFavorites = true,
}) => {
  const { user, loading } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMessage, setAuthMessage] = useState(
    'Using filters is a feature for users that have accounts. Would you like to create an account or log in?',
  );
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(
    initialFilters.serviceTypes || [],
  );
  const [selectedPriceRanges, setSelectedPriceRanges] = useState(
    initialFilters.priceRanges || [],
  );
  const lastNotifiedFiltersRef = useRef(null);

  // Dialog handlers
  const handleClose = () => {
    setShowAuthDialog(false);
  };

  const defaultAuthMessage =
    'Using filters is a feature for users that have accounts. Would you like to create an account or log in?';

  // Check authentication before allowing filter changes
  const checkAuthAndExecute = (
    callback,
    { requireAuth = requireAuthForFilters, message = defaultAuthMessage } = {},
  ) => {
    if (!requireAuth) {
      callback();
      return;
    }

    // Don't show auth modal if still loading
    if (loading) {
      return;
    }

    // Show auth modal for non-authenticated users but don't execute filter change
    if (!user) {
      setAuthMessage(message);
      setShowAuthDialog(true);
      return;
    }

    // Only execute the callback if user is authenticated
    callback();
  };

  // Clear all filters
  const handleClearFilters = () => {
    checkAuthAndExecute(() => {
      setSelectedServiceTypes([]);
      setSelectedPriceRanges([]);
      if (showFavoritesFilter && favoritesOnly) {
        onFavoritesOnlyChange?.(false);
      }
    });
  };

  const handleFavoritesToggle = () => {
    checkAuthAndExecute(
      () => {
        onFavoritesOnlyChange?.(!favoritesOnly);
      },
      {
        requireAuth: requireAuthForFavorites,
        message:
          'Saving and filtering favorite partners requires an account. Would you like to create an account or log in?',
      },
    );
  };

  // Notify parent when filter selections change (skip duplicate payloads).
  useEffect(() => {
    const payload = {
      serviceTypes: selectedServiceTypes,
      priceRanges: selectedPriceRanges,
    };
    const signature = JSON.stringify({
      serviceTypes: payload.serviceTypes.map((s) => s.value).sort(),
      priceRanges: payload.priceRanges,
    });
    if (lastNotifiedFiltersRef.current === signature) return;
    lastNotifiedFiltersRef.current = signature;
    onFilterChange(payload);
  }, [selectedServiceTypes, selectedPriceRanges, onFilterChange]);

  return (
    <FilterContainer>
      <FilterRow>
        {showServiceTypeFilter && (
          <ServiceTypeFilter
            value={selectedServiceTypes}
            onChange={(newValue) => {
              checkAuthAndExecute(() => {
                setSelectedServiceTypes(newValue);
              });
            }}
            businesses={businesses}
          />
        )}

        {showPriceFilter && (
          <PriceCheckboxFilter
            value={selectedPriceRanges}
            onChange={(newValue) => {
              checkAuthAndExecute(() => {
                setSelectedPriceRanges(newValue);
              });
            }}
          />
        )}

        {showFavoritesFilter && (
          <FavoritesToggle
            type="button"
            $active={favoritesOnly}
            aria-pressed={favoritesOnly}
            aria-label={
              favoritesOnly
                ? 'Show all partners'
                : 'Show favorite partners only'
            }
            onClick={handleFavoritesToggle}
          >
            {favoritesOnly ? <StarIcon /> : <StarBorderIcon />}
            Favorites
          </FavoritesToggle>
        )}

        <ClearButton
          variant="outlined"
          onClick={handleClearFilters}
          size="small"
          $hasFavoritesToggle={showFavoritesFilter}
        >
          Clear filters
        </ClearButton>
      </FilterRow>

      <AuthModal
        open={showAuthDialog}
        onClose={handleClose}
        message={authMessage}
      />
    </FilterContainer>
  );
};

export default BusinessFilter;
